/***
 * Строготипизированная замена для Boolean(...)
 * Обычно используется для [...].filter(isNotNullOrUndefined)
 */
export function isNotNullOrUndefined<T>(input: T): input is NonNullable<T> {
    return input != undefined;
}

export function stringifyUnknownError(error: unknown): undefined | string {
    // @ts-ignore
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return typeof error === "object" && error?.["message"];
}

export function castToError(unknown: unknown): Error {
    return unknown instanceof Error ? unknown : new Error(String(unknown));
}

export function ensureNotNullOrUndefined<T>(input: T, message?: string): asserts input is NonNullable<T> {
    if (!isNotNullOrUndefined(input)) {
        throw new Error(message ?? `Value must be defined`);
    }
}

export function ensureHasFunctionType(input: unknown, message?: string): asserts input is Function {
    if (typeof input !== "function") {
        throw new Error(message ?? `Value must be defined`);
    }
}

export function noop(): void {
    // noop
}

export type ErrorFirstCallback<T> = (...args: [Error, null | undefined] | [null | undefined, T] | [Error]) => void;

export function reject(message?: string | Error): never {
    if (message instanceof Error) {
        throw message;
    }
    throw new Error(message ?? "Undefined is not allowed");
}

type ArrayOfLength<TItem, TLength extends number, TRest extends TItem[] = []> = TRest extends { length: TLength }
    ? TRest
    : ArrayOfLength<TItem, TLength, [TItem, ...TRest]>;

export type MinLengthArray<TItem, TLength extends number, TRest extends TItem[] = []> = TRest extends {
    length: TLength;
}
    ? [...TRest, ...TItem[]]
    : MinLengthArray<TItem, TLength, [...TRest, TItem]>;

export function isLengthGreaterThenOrEqual<T, TNum extends number>(
    items: readonly T[],
    minLength: TNum
): items is MinLengthArray<T, TNum> {
    return items.length >= minLength;
}

export function minLengthOrUndefined<T, TNum extends number>(
    items: T[],
    minLength: TNum
): undefined | MinLengthArray<T, TNum> {
    if (isLengthGreaterThenOrEqual(items, minLength)) {
        return items;
    }
    return undefined;
}

export function ensureIsArray<T>(items: unknown): T[] {
    if (!Array.isArray(items)) {
        throw new Error(`Must be an array!`);
    }
    return items as T[];
}

export function arrayHasLength<T, TNum extends number>(
    items: readonly T[],
    length: TNum
): items is ArrayOfLength<T, TNum> {
    return items.length === length;
}

export function arrayIsNotEmpty<T>(items: readonly T[]): items is MinLengthArray<T, 1> {
    return items.length > 0;
}

export function arrayHasAtLeastOneItem<T>(items: readonly T[]): items is MinLengthArray<T, 1> {
    return items.length > 0;
}

export function ensureArrayIsNotEmpty<T>(items: readonly T[], message?: string): asserts items is MinLengthArray<T, 1> {
    if (!arrayIsNotEmpty(items)) {
        throw new Error(message ?? `Array must be not empty`);
    }
}

export function ensureArrayHasLength<T, TNum extends number>(
    items: readonly T[],
    length: TNum
): asserts items is ArrayOfLength<T, TNum> {
    if (!arrayHasLength(items, length)) {
        throw new Error(`Array should has length ${length}`);
    }
}

export function ensureArrayLengthGreaterThenOrEqual<T, TNum extends number>(
    items: readonly T[],
    length: TNum
): asserts items is ArrayOfLength<T, TNum> {
    if (!isLengthGreaterThenOrEqual(items, length)) {
        throw new Error(`Array must have minimal length ${length}`);
    }
}

// проверяет, что родительский тип находится в массиве дочерних
export const isOneOf = <Value, Item extends Value>(value: Value, array: Item[]): value is Item =>
    array.includes(value as Item);

export function ensureNever(_neverArgument: never): void {
    // noop
}

export function ensureAndGetString(neverArgument: never): string {
    return `${neverArgument}`;
}

export function runAsyncAction(action: () => Promise<void>): void {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    action();
}

export function isNullOrEmpty(input: string | undefined): input is undefined {
    return input == undefined || input === "";
}

export function isNotNullOrEmpty(input: string | undefined): input is string {
    return !isNullOrEmpty(input);
}

export function isNullOrWhiteSpace(input: string | undefined | null): input is undefined | null {
    return input == undefined || input.trim() === "";
}

export function trimLastSlash(url: string): string {
    return url.slice(-1) === "/" ? url.slice(0, -1) : url;
}

export function assertIs<T>(_: T): void {
    // noop
}

export type DeepPartial<T> = {
    [P in keyof T]?: T[P] extends Array<infer U>
        ? Array<DeepPartial<U>> | { length?: number; [key: number]: DeepPartial<U> }
        : T[P] extends ReadonlyArray<infer U>
          ? ReadonlyArray<DeepPartial<U>>
          : DeepPartial<T[P]>;
};
